import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Calendar, User, ChevronDown, ChevronRight, Search, Plus, Save,
  X, Check, Trash2, FileText, Filter, Pencil, DollarSign, Flag, AlertTriangle, RotateCcw, Send, Eraser, Radio, Undo2
} from "lucide-react";
import WeeklyClosingSection from "./WeeklyClosingSection";
import PhaseCBanner from "./PhaseCBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ACTION_OPTIONS = [
  { value: "Completed", label: "Completed", cls: "bg-[#6B8F62]/10 text-[#6B8F62] border-[#6B8F62]/25" },
  { value: "Hold", label: "Hold", cls: "bg-[#D1A054]/10 text-[#7A6535] border-[#D1A054]/30" },
  { value: "Returned", label: "Returned", cls: "bg-[#C07070]/10 text-[#C07070] border-[#C07070]/25" },
  { value: "Not Done", label: "Not Done", cls: "bg-gray-100 text-gray-500 border-gray-300" },
  { value: "Follow Up", label: "Follow Up", cls: "bg-[#C07070]/10 text-[#C07070] border-[#C07070]/25" },
  { value: "Transmitted Next Day", label: "Transmitted Next Day", cls: "bg-[#3B7A9C]/10 text-[#3B7A9C] border-[#3B7A9C]/25" },
  { value: "Cancelled", label: "Cancelled", cls: "bg-[#8B4545]/10 text-[#8B4545] border-[#8B4545]/20" },
  { value: "Rescheduled", label: "Rescheduled", cls: "bg-[#2F5D8A]/10 text-[#2F5D8A] border-[#2F5D8A]/25" },
  { value: "Transmission Next Day", label: "Transmission Next Day", cls: "bg-[#B08A3E]/10 text-[#B08A3E] border-[#B08A3E]/25" },
  { value: "Transmission in 2 Days", label: "Transmission in 2 Days", cls: "bg-[#9A7735]/10 text-[#9A7735] border-[#9A7735]/25" },
  { value: "Internal flipped to Exterior", label: "Internal flipped to Exterior", cls: "bg-[#7A6B8F]/10 text-[#7A6B8F] border-[#7A6B8F]/25" },
];

const ActionBadge = ({ action }) => {
  const opt = ACTION_OPTIONS.find(a => a.value === action);
  const cls = opt?.cls || "bg-gray-100 text-gray-500 border-gray-300";
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cls}`}>{action}</span>;
};

function RouteArchivePanel({ inspector, routeDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("planned");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/daily-closing/route-archive`, { params: { inspector, date: routeDate } });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  }, [inspector, routeDate]);

  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const files = data?.route_files || [];
  const planned = data?.planned || [];
  const closed = data?.closed || [];
  const diff = data?.diff || { matched_count: 0, missing_in_route: [], missed_on_route: [] };

  return (
    <div className="border-t border-dashed border-gray-200 bg-[#FAFAF6]" data-testid={`route-archive-${inspector}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-[#1F2937] hover:bg-[#F5F1E8]/60 transition-colors">
        <span className="flex items-center gap-2">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-[#B08A3E]" /> : <ChevronRight className="h-3.5 w-3.5 text-[#B08A3E]" />}
          <FileText className="h-3.5 w-3.5 text-[#B08A3E]" />
          <span className="font-semibold uppercase tracking-wider text-[11px] text-[#B08A3E]">Route Archive · {routeDate}</span>
          {data && (
            <span className="text-[10px] text-gray-500">
              <b>{planned.length}</b> planned · <b>{closed.length}</b> closed · <b className={diff.missed_on_route.length > 0 ? "text-[#C07070]" : "text-[#6B8F62]"}>{diff.matched_count}</b> matched
            </span>
          )}
        </span>
        {!open && <span className="text-[10px] text-gray-400">click to load audit</span>}
      </button>

      {open && (
        <div className="px-4 pb-3 space-y-2">
          {loading && <p className="text-xs text-gray-400 py-3 text-center">Loading audit…</p>}
          {err && <p className="text-xs text-red-600 py-2">{err}</p>}
          {data && (
            <>
              {files.length === 0 && (
                <p className="text-[11px] text-gray-400 italic py-2">No route file was uploaded for this inspector on or before {routeDate}.</p>
              )}
              {files.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Planned route source:</span>
                  {files.slice(0, 3).map((f, i) => (
                    <a key={i} href={`${API}/route/download/${f.stored_filename}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px] text-[#2F5D8A] hover:bg-[#2F5D8A]/5"
                      data-testid={`route-archive-file-${i}`}>
                      <FileText className="h-3 w-3" />
                      <span className="max-w-[160px] truncate">{f.original_filename}</span>
                      {f.file_type && (
                        <span className={`ml-0.5 px-1 py-0 rounded text-[8px] font-bold uppercase ${
                          f.file_type === "gpx" ? "bg-[#6B8F62]/20 text-[#4F7046]" : f.file_type === "pdf" ? "bg-[#C07070]/20 text-[#A15252]" : "bg-gray-200 text-gray-600"
                        }`}>{f.file_type}</span>
                      )}
                    </a>
                  ))}
                </div>
              )}

              {/* 3-tile summary */}
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => setTab("planned")}
                  className={`rounded-lg border p-2.5 text-left transition-all ${tab === "planned" ? "border-[#2F5D8A] ring-1 ring-[#2F5D8A]/30 bg-white" : "border-gray-200 bg-white hover:border-gray-300"}`}
                  data-testid="route-archive-tab-planned">
                  <p className="text-[9px] uppercase font-bold text-[#2F5D8A]">Planned</p>
                  <p className="text-xl font-bold text-[#2F5D8A]">{planned.length}</p>
                  <p className="text-[9px] text-gray-400">from uploaded route</p>
                </button>
                <button type="button" onClick={() => setTab("closed")}
                  className={`rounded-lg border p-2.5 text-left transition-all ${tab === "closed" ? "border-[#6B8F62] ring-1 ring-[#6B8F62]/30 bg-white" : "border-gray-200 bg-white hover:border-gray-300"}`}
                  data-testid="route-archive-tab-closed">
                  <p className="text-[9px] uppercase font-bold text-[#6B8F62]">Closed</p>
                  <p className="text-xl font-bold text-[#6B8F62]">{closed.length}</p>
                  <p className="text-[9px] text-gray-400">actual work</p>
                </button>
                <button type="button" onClick={() => setTab("diff")}
                  className={`rounded-lg border p-2.5 text-left transition-all ${tab === "diff" ? "border-[#C07070] ring-1 ring-[#C07070]/30 bg-white" : "border-gray-200 bg-white hover:border-gray-300"} ${(diff.missed_on_route.length + diff.missing_in_route.length) > 0 ? "bg-[#C07070]/5" : ""}`}
                  data-testid="route-archive-tab-diff">
                  <p className="text-[9px] uppercase font-bold text-[#C07070]">Diff</p>
                  <p className="text-xl font-bold text-[#C07070]">{diff.missed_on_route.length + diff.missing_in_route.length}</p>
                  <p className="text-[9px] text-gray-400">off-plan</p>
                </button>
              </div>

              <div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                <div className="max-h-[240px] overflow-y-auto">
                  {tab === "planned" && (
                    <table className="w-full text-[10px]">
                      <thead className="sticky top-0 bg-gray-50"><tr className="border-b">
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-500">#</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Planned stop</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Order</th>
                      </tr></thead>
                      <tbody>
                        {planned.length === 0 ? <tr><td colSpan={3} className="px-2 py-3 text-center text-gray-400 italic">No planned stops.</td></tr> :
                          planned.map((p, i) => (
                            <tr key={i} className="border-b border-gray-50">
                              <td className="px-2 py-1 font-mono text-gray-400">{i + 1}</td>
                              <td className="px-2 py-1 text-gray-700">{p.raw}</td>
                              <td className="px-2 py-1 text-gray-500 font-mono">{p.order_number || "—"}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                  {tab === "closed" && (
                    <table className="w-full text-[10px]">
                      <thead className="sticky top-0 bg-gray-50"><tr className="border-b">
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Order</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Address</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Action</th>
                      </tr></thead>
                      <tbody>
                        {closed.length === 0 ? <tr><td colSpan={3} className="px-2 py-3 text-center text-gray-400 italic">Nothing closed.</td></tr> :
                          closed.map((c, i) => (
                            <tr key={i} className="border-b border-gray-50">
                              <td className="px-2 py-1 font-mono text-gray-700">{c.order_number || "—"}</td>
                              <td className="px-2 py-1 text-gray-600">{c.address || "—"}</td>
                              <td className="px-2 py-1"><ActionBadge action={c.action || "—"} /></td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                  {tab === "diff" && (
                    <div className="divide-y divide-gray-100">
                      <div>
                        <div className="bg-[#C07070]/10 px-3 py-1.5 text-[10px] font-bold uppercase text-[#A15252]">Closed but not on planned route ({diff.missing_in_route.length})</div>
                        {diff.missing_in_route.length === 0 ? <p className="px-3 py-2 text-[10px] text-gray-400 italic">All closed orders matched the planned route.</p> :
                          <table className="w-full text-[10px]"><tbody>
                            {diff.missing_in_route.map((c, i) => (
                              <tr key={i} className="border-b border-gray-50"><td className="px-2 py-1 font-mono text-gray-700 w-24">{c.order_number || "—"}</td><td className="px-2 py-1 text-gray-600">{c.address || "—"}</td><td className="px-2 py-1"><ActionBadge action={c.action || "—"} /></td></tr>
                            ))}
                          </tbody></table>}
                      </div>
                      <div>
                        <div className="bg-[#D97706]/10 px-3 py-1.5 text-[10px] font-bold uppercase text-[#8B5A07]">Planned but NOT closed ({diff.missed_on_route.length})</div>
                        {diff.missed_on_route.length === 0 ? <p className="px-3 py-2 text-[10px] text-gray-400 italic">Every planned stop was closed.</p> :
                          <table className="w-full text-[10px]"><tbody>
                            {diff.missed_on_route.map((p, i) => (
                              <tr key={i} className="border-b border-gray-50"><td className="px-2 py-1 font-mono text-gray-500 w-24">{p.order_number || "—"}</td><td className="px-2 py-1 text-gray-600">{p.raw}</td></tr>
                            ))}
                          </tbody></table>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


const DailyClosingPage = ({ Layout }) => {
  const [activeTab, setActiveTab] = useState("daily");
  const [routeDate, setRouteDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [inspectors, setInspectors] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState("");
  const [summary, setSummary] = useState([]);
  const [specialBucketCounts, setSpecialBucketCounts] = useState({}); // DAILY-CLOSE-RULE-001
  const [totalClosingsVisible, setTotalClosingsVisible] = useState(0);
  const [loading, setLoading] = useState(false);
  const [existingDates, setExistingDates] = useState([]);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [inspectorOrders, setInspectorOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState(new Map());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedInsp, setExpandedInsp] = useState(null);
  // Sprint B-2 (Angela 2026-05-18) — batch close in Daily Close
  const [batchSelected, setBatchSelected] = useState(new Set());
  const [batchAction, setBatchAction] = useState("");
  const [batchReason, setBatchReason] = useState("");
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);

  // Manual inspector payment editing
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentValue, setPaymentValue] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");

  // Table filters state
  const [showFilters, setShowFilters] = useState(false);
  const [tableFilters, setTableFilters] = useState({
    company: "", client: "", city: "", inspector: "", action: "", transmission_date: "", search: "",
  });

  // Inline edit state
  const [editingEntry, setEditingEntry] = useState(null); // { order_id, inspector_name, route_date }
  const [editAction, setEditAction] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // Follow-up toggle state
  const [togglingFollowUp, setTogglingFollowUp] = useState(null);
  const [followUpComment, setFollowUpComment] = useState("");
  const [followUpReason, setFollowUpReason] = useState("");
  // Inline transmission date editing
  const [editingTransDate, setEditingTransDate] = useState(null);
  const [editTransDateValue, setEditTransDateValue] = useState("");

  // Reopen from Daily Closing
  const [reopenModal, setReopenModal] = useState(null); // { order_id, order_number, inspector_name }
  const [reopenDest, setReopenDest] = useState("to_dispatch");
  const [reopenReason, setReopenReason] = useState("");
  const [reopenProcessing, setReopenProcessing] = useState(false);

  // Update Transmission modal (same UX as OrderDetailPage transmit modal).
  // Opens for a specific closed order; reuses POST /api/orders/{id}/transmit.
  const [transmitModal, setTransmitModal] = useState(null); // { order_id, order_number, transmitted, transmitted_by, transmission_paid_by }
  const [transmitOperator, setTransmitOperator] = useState("");
  const [transmitPaidBy, setTransmitPaidBy] = useState("");
  const [transmitOfficeReason, setTransmitOfficeReason] = useState("");
  const [transmitOfficeReasonDetail, setTransmitOfficeReasonDetail] = useState("");
  const [transmitSubmitting, setTransmitSubmitting] = useState(false);

  const openTransmitModal = (order) => {
    setTransmitOperator(order.transmitted_by || "");
    setTransmitPaidBy(order.transmission_paid_by || "");
    setTransmitOfficeReason(order.office_payment_reason || "");
    setTransmitOfficeReasonDetail(order.office_payment_reason_detail || "");
    setTransmitModal({
      order_id: order.order_id,
      order_number: order.order_number,
      transmitted: order.transmitted === "yes" || !!order.transmitted_by,
    });
  };

  const confirmTransmit = async () => {
    if (!transmitModal) return;
    if (!transmitOperator || !transmitPaidBy) return;
    if (transmitPaidBy === "Office" && !transmitOfficeReason) return;
    if (transmitPaidBy === "Office" && transmitOfficeReason === "Other" && !transmitOfficeReasonDetail.trim()) return;
    setTransmitSubmitting(true);
    const ids = transmitModal._bulk_order_ids && transmitModal._bulk_order_ids.length > 0
      ? transmitModal._bulk_order_ids
      : [transmitModal.order_id];
    const today = new Date().toISOString().slice(0, 10);
    let succeeded = 0;
    let failed = 0;
    for (const oid of ids) {
      try {
        await axios.post(`${API}/orders/${oid}/transmit`, {
          transmitted_by_operator: transmitOperator,
          paid_by: transmitPaidBy,
          office_reason: transmitPaidBy === "Office" ? transmitOfficeReason : null,
          office_reason_detail: transmitPaidBy === "Office" && transmitOfficeReason === "Other" ? transmitOfficeReasonDetail : null,
        });
        // Sync the Daily Closing transmission_date column so the timeline matches.
        try {
          const entry = allOrders.find(o => o.order_id === oid);
          await axios.patch(`${API}/daily-closings/update-entry`, {
            order_id: oid,
            route_date: routeDate,
            inspector_name: (entry && entry.inspector_display) || transmitModal._inspector_name || "",
            transmission_date: today,
          });
        } catch { /* non-fatal */ }
        succeeded += 1;
      } catch (err) {
        failed += 1;
      }
    }
    setTransmitSubmitting(false);
    setTransmitModal(null);
    setSelectedClosingIds(new Set());
    if (failed === 0) {
      setMessage({ type: "success", text: `Transmission saved for ${succeeded} order(s) by ${transmitOperator}.` });
    } else {
      setMessage({ type: "error", text: `Saved ${succeeded}, failed ${failed}. Check permissions or retry.` });
    }
    fetchSummary();
  };

  const undoTransmit = async () => {
    if (!transmitModal) return;
    if (!window.confirm("Undo transmission for this order?")) return;
    setTransmitSubmitting(true);
    try {
      await axios.post(`${API}/orders/${transmitModal.order_id}/undo-transmit`);
      setMessage({ type: "success", text: `Transmission reverted for #${transmitModal.order_number}.` });
      setTransmitModal(null);
      fetchSummary();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to undo transmission" });
    } finally {
      setTransmitSubmitting(false);
    }
  };

  // Clear Daily Closing
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearScope, setClearScope] = useState("all"); // "all" or specific inspector
  const [clearReason, setClearReason] = useState("");
  const [clearProcessing, setClearProcessing] = useState(false);

  // Batch Route Date Edit
  const [selectedClosingIds, setSelectedClosingIds] = useState(new Set());
  const [showRouteDateEdit, setShowRouteDateEdit] = useState(false);
  const [newRouteDate, setNewRouteDate] = useState("");
  const [routeDateEditProcessing, setRouteDateEditProcessing] = useState(false);

  const fetchInspectors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inspectors`);
      setInspectors(res.data || []);
    } catch {}
  }, []);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, datesRes] = await Promise.all([
        axios.get(`${API}/daily-closings/summary`, { params: { route_date: routeDate } }),
        axios.get(`${API}/daily-closings/dates`),
      ]);
      // DAILY-CLOSE-RULE-001 (2026-05-18): backend now returns
      // {inspectors, special_bucket_counts, rule, total_closings_visible}.
      // Old shape was a bare array — keep backward-compat with both.
      const data = sumRes.data;
      if (data && Array.isArray(data.inspectors)) {
        setSummary(data.inspectors);
        setSpecialBucketCounts(data.special_bucket_counts || {});
        setTotalClosingsVisible(data.total_closings_visible || 0);
      } else {
        setSummary(data || []);
        setSpecialBucketCounts({});
        setTotalClosingsVisible(0);
      }
      setExistingDates(datesRes.data || []);
    } catch {}
    setLoading(false);
  }, [routeDate]);

  useEffect(() => { fetchInspectors(); }, [fetchInspectors]);
  useEffect(() => { fetchSummary(); }, [routeDate, fetchSummary]);

  // Search orders
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/daily-closings/search-orders`, { params: { q: searchQuery } });
        setSearchResults(res.data || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Fetch inspector orders
  useEffect(() => {
    if (!selectedInspector) { setInspectorOrders([]); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/daily-closings/inspector-orders`, { params: { inspector: selectedInspector } });
        setInspectorOrders(res.data || []);
      } catch {}
    })();
  }, [selectedInspector]);

  const toggleOrder = (order) => {
    setSelectedOrders(prev => {
      const next = new Map(prev);
      next.has(order.id) ? next.delete(order.id) : next.set(order.id, { order, action: "Completed", notes: "" });
      return next;
    });
  };

  const updateOrderAction = (orderId, action) => {
    setSelectedOrders(prev => {
      const next = new Map(prev);
      const entry = next.get(orderId);
      if (entry) next.set(orderId, { ...entry, action });
      return next;
    });
  };

  const updateOrderNotes = (orderId, notes) => {
    setSelectedOrders(prev => {
      const next = new Map(prev);
      const entry = next.get(orderId);
      if (entry) next.set(orderId, { ...entry, notes });
      return next;
    });
  };

  const handleSave = async () => {
    if (selectedOrders.size === 0 || !selectedInspector) return;
    setSaving(true);
    setMessage(null);
    try {
      const orders = Array.from(selectedOrders.values()).map(e => ({
        order_id: e.order.id, action: e.action, notes: e.notes,
      }));
      const res = await axios.post(`${API}/daily-closings`, {
        route_date: routeDate, inspector_name: selectedInspector, orders,
      });
      setMessage({ type: "success", text: `${res.data.created} orders saved to closing for ${selectedInspector}` });
      setSelectedOrders(new Map());
      setShowAddPanel(false);
      fetchSummary();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Save failed" });
    } finally { setSaving(false); }
  };

  const handleDeleteClosing = async (inspector) => {
    if (!window.confirm(`Delete all closing entries for ${inspector} on ${routeDate}?`)) return;
    try {
      await axios.delete(`${API}/daily-closings`, { params: { route_date: routeDate, inspector } });
      setMessage({ type: "success", text: `Deleted closing for ${inspector}` });
      fetchSummary();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Delete failed" });
    }
  };

  // Inline edit handlers
  const startEdit = (order, inspectorName) => {
    setEditingEntry({ order_id: order.order_id, inspector_name: inspectorName, route_date: routeDate });
    setEditAction(order.action);
    setEditNotes(order.notes || "");
  };

  const cancelEdit = () => { setEditingEntry(null); };

  const saveEdit = async () => {
    if (!editingEntry) return;
    try {
      await axios.patch(`${API}/daily-closings/update-entry`, {
        order_id: editingEntry.order_id,
        route_date: editingEntry.route_date,
        inspector_name: editingEntry.inspector_name,
        action: editAction,
        notes: editNotes,
      });
      setEditingEntry(null);
      fetchSummary();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Update failed" });
    }
  };

  // Follow-up flag toggle - now requires reason choice
  const handleToggleFollowUp = async (order, inspName, newValue) => {
    if (newValue) {
      setTogglingFollowUp({ order_id: order.order_id, inspector_name: inspName, route_date: routeDate });
      setFollowUpComment("");
      setFollowUpReason("");
      return;
    }
    // Remove follow-up
    try {
      await axios.patch(`${API}/daily-closings/update-entry`, {
        order_id: order.order_id, route_date: routeDate, inspector_name: inspName,
        follow_up: false, follow_up_comment: "",
      });
      setTogglingFollowUp(null);
      fetchSummary();
    } catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
  };

  const confirmFollowUp = async () => {
    if (!togglingFollowUp || !followUpReason) return;
    try {
      await axios.patch(`${API}/daily-closings/update-entry`, {
        ...togglingFollowUp,
        follow_up: true,
        follow_up_comment: `${followUpReason}${followUpComment ? ": " + followUpComment : ""}`,
        follow_up_reason: followUpReason,
      });
      setTogglingFollowUp(null);
      setFollowUpComment("");
      setFollowUpReason("");
      fetchSummary();
    } catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
  };

  // Save manual inspector payment
  const saveInspectorPayment = async (inspectorName, payType) => {
    try {
      const payload = {
        inspector_name: inspectorName,
        route_date: routeDate,
        payment_type: payType || "per_order",
        notes: paymentNotes,
      };
      if (payType === "full_day") {
        // Full Day: save to full_day_amount, don't touch amount
        payload.full_day_amount = paymentValue ? parseFloat(paymentValue) : null;
      } else {
        // Per Order: save to amount
        payload.amount = paymentValue ? parseFloat(paymentValue) : null;
      }
      await axios.patch(`${API}/daily-closings/inspector-payment`, payload);
      setEditingPayment(null);
      setPaymentValue("");
      setPaymentNotes("");
      fetchSummary();
      setMessage({ type: "success", text: `Payment saved for ${inspectorName}` });
    } catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
  };

  const togglePaymentType = async (inspectorName, currentType) => {
    const newType = currentType === "full_day" ? "per_order" : "full_day";
    if (newType === "full_day") {
      // Switch to full_day — save type immediately, then open editor
      try {
        await axios.patch(`${API}/daily-closings/inspector-payment`, {
          inspector_name: inspectorName,
          route_date: routeDate,
          payment_type: "full_day",
          full_day_amount: null,
          notes: "",
        });
        await fetchSummary();
        setEditingPayment(inspectorName);
        setPaymentValue("");
        setPaymentNotes("");
      } catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
    } else {
      // Switch to per_order — clear the full_day_amount
      try {
        await axios.patch(`${API}/daily-closings/inspector-payment`, {
          inspector_name: inspectorName,
          route_date: routeDate,
          amount: null,
          full_day_amount: null,
          payment_type: "per_order",
          notes: "",
        });
        fetchSummary();
        setMessage({ type: "success", text: `${inspectorName} set to Per Order` });
      } catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
    }
  };

  // Inline transmission date save
  const saveTransmissionDate = async (order, inspName) => {
    try {
      await axios.patch(`${API}/daily-closings/update-entry`, {
        order_id: order.order_id,
        route_date: routeDate,
        inspector_name: inspName,
        transmission_date: editTransDateValue,
      });
      setEditingTransDate(null);
      setEditTransDateValue("");
      fetchSummary();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Transmission date update failed" });
    }
  };

  // Reopen order from Daily Closing
  const handleReopen = async () => {
    if (!reopenModal) return;
    setReopenProcessing(true);
    try {
      await axios.post(`${API}/orders/${reopenModal.order_id}/reopen`, {
        destination: reopenDest,
        reopen_reason: reopenReason.trim() || "Reopened from Daily Closing",
        inspector_name: reopenDest === "to_inspector" ? reopenModal.inspector_name : undefined,
      });
      const destLabel = reopenDest === "to_inspector" ? `Inspector (${reopenModal.inspector_name})` : "Dispatch";
      setMessage({ type: "success", text: `Order ${reopenModal.order_number || reopenModal.order_id.slice(-6)} reopened → ${destLabel}` });
      setReopenModal(null);
      setReopenDest("to_dispatch");
      setReopenReason("");
      fetchSummary();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Reopen failed" });
    } finally {
      setReopenProcessing(false);
    }
  };

  // Clear Daily Closing (safe reset — archives records)
  const handleClearClosing = async () => {
    setClearProcessing(true);
    try {
      const payload = {
        route_date: routeDate,
        reason: clearReason.trim() || "Daily Closing Cleared",
      };
      if (clearScope !== "all") {
        payload.inspector_name = clearScope;
      }
      const res = await axios.post(`${API}/daily-closings/clear`, payload);
      const scopeLabel = clearScope === "all" ? `all inspectors on ${routeDate}` : `${clearScope} on ${routeDate}`;
      setMessage({ type: "success", text: `Cleared ${res.data.cleared} entries for ${scopeLabel}. Records archived safely.` });
      setShowClearModal(false);
      setClearScope("all");
      setClearReason("");
      fetchSummary();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Clear failed" });
    } finally {
      setClearProcessing(false);
    }
  };

  const formatDate = (ds) => {
    if (!ds) return "";
    try {
      const d = new Date(ds + "T00:00:00");
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      return `${days[d.getDay()]}, ${ds}`;
    } catch { return ds; }
  };

  const toggleClosingSelect = (orderId) => {
    setSelectedClosingIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const handleBatchRouteDateUpdate = async () => {
    if (selectedClosingIds.size === 0 || !newRouteDate) return;
    setRouteDateEditProcessing(true);
    try {
      // Find inspector for these orders
      const inspName = allOrders.find(o => selectedClosingIds.has(o.order_id))?.inspector_display || "";
      const res = await axios.patch(`${API}/daily-closings/batch-update-route-date`, {
        order_ids: Array.from(selectedClosingIds),
        old_route_date: routeDate,
        new_route_date: newRouteDate,
        inspector_name: inspName,
      });
      setMessage({ type: "success", text: `Route date updated to ${newRouteDate} for ${res.data.updated_closings} entries.` });
      setSelectedClosingIds(new Set());
      setShowRouteDateEdit(false);
      setNewRouteDate("");
      fetchSummary();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Route date update failed" });
    } finally {
      setRouteDateEditProcessing(false);
    }
  };

  // All orders flat for filtering
  const allOrders = useMemo(() => {
    const flat = [];
    summary.forEach(g => {
      g.orders.forEach(o => flat.push({ ...o, inspector_display: g.inspector }));
    });
    return flat;
  }, [summary]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return allOrders.filter(o => {
      if (tableFilters.company && !o.company?.toLowerCase().includes(tableFilters.company.toLowerCase())) return false;
      if (tableFilters.client && !o.client?.toLowerCase().includes(tableFilters.client.toLowerCase())) return false;
      if (tableFilters.city && !o.city?.toLowerCase().includes(tableFilters.city.toLowerCase())) return false;
      if (tableFilters.inspector && !o.inspector_display?.toLowerCase().includes(tableFilters.inspector.toLowerCase())) return false;
      if (tableFilters.action && o.action !== tableFilters.action) return false;
      if (tableFilters.transmission_date && o.transmission_date !== tableFilters.transmission_date) return false;
      if (tableFilters.search) {
        const q = tableFilters.search.toLowerCase();
        if (!(o.order_number || "").toLowerCase().includes(q) && !(o.address || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allOrders, tableFilters]);

  const hasActiveFilters = Object.values(tableFilters).some(v => v);

  // DAILY-CLOSE-RULE-001 (Angela 2026-05-18) — partition the summary
  // between real-inspector groups and special-bucket groups so UI counters
  // can show both totals without inflating "9 inspectors" with badge rows.
  const regularInspectors = summary.filter(g => !g.is_special_bucket);
  const specialBucketGroups = summary.filter(g => g.is_special_bucket);
  const totalOrders = summary.reduce((s, g) => s + g.orders.length, 0);
  const totalRegularOrders = regularInspectors.reduce((s, g) => s + g.orders.length, 0);
  const totalSpecialBucketOrders = specialBucketGroups.reduce((s, g) => s + g.orders.length, 0);
  const totalFollowUpOrders = summary.reduce((s, g) => s + g.orders.filter(o => o.follow_up).length, 0);

  const existingIds = useMemo(() => {
    const ids = new Set();
    summary.forEach(g => {
      if (g.inspector === selectedInspector) g.orders.forEach(o => ids.add(o.order_id));
    });
    return ids;
  }, [summary, selectedInspector]);

  // Unique values for filter dropdowns
  const uniqueCompanies = useMemo(() => [...new Set(allOrders.map(o => o.company).filter(Boolean))], [allOrders]);
  const uniqueActions = useMemo(() => [...new Set(allOrders.map(o => o.action).filter(Boolean))], [allOrders]);
  const uniqueCities = useMemo(() => [...new Set(allOrders.map(o => o.city).filter(Boolean))].sort(), [allOrders]);
  const uniqueInspectors = useMemo(() => [...new Set(allOrders.map(o => o.inspector_display).filter(Boolean))].sort(), [allOrders]);

  const isEditing = (o, inspName) => editingEntry?.order_id === o.order_id && editingEntry?.inspector_name === inspName;

  const renderOrderRow = (o, inspName, idx) => {
    const editing = isEditing(o, inspName);
    const hasFollowUp = !!o.follow_up;
    const isEditingTrans = editingTransDate === `${o.order_id}-${inspName}`;
    return (
      <tr key={`${o.order_id}-${idx}`} className={`border-t border-gray-50 hover:bg-gray-50/50 group ${hasFollowUp ? "bg-[#C07070]/[0.03]" : ""}`} data-testid={`closing-row-${o.order_id}`}>
        <td className="px-2 py-2 w-8">
          <input type="checkbox" checked={selectedClosingIds.has(o.order_id)}
            onChange={() => toggleClosingSelect(o.order_id)}
            className="rounded border-gray-300 text-[#2F5D8A] focus:ring-[#2F5D8A] h-3.5 w-3.5"
            data-testid={`closing-select-${o.order_id}`}
          />
        </td>
        <td className="px-3 py-2 font-medium text-[#1F2937] text-xs">
          {o.order_number || o.order_id?.slice(-6)}
          {o.manual_entry && <span className="ml-1 px-1 py-0.5 bg-[#B08A3E]/10 text-[#B08A3E] text-[8px] font-bold uppercase rounded">Manual</span>}
        </td>
        <td className="px-3 py-2 text-xs text-gray-600">{o.company}</td>
        <td className="px-3 py-2 text-xs text-gray-600 max-w-[100px] truncate" title={o.client}>{o.client || "-"}</td>
        <td className="px-3 py-2 text-xs text-gray-500 max-w-[160px] truncate" title={o.address}>{o.address}</td>
        <td className="px-3 py-2">
          {editing ? (
            <select value={editAction} onChange={e => setEditAction(e.target.value)}
              className="px-1.5 py-0.5 bg-white border border-[#B08A3E] rounded text-xs text-[#1F2937] focus:outline-none"
              data-testid="edit-action-select"
            >
              {ACTION_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          ) : (
            <ActionBadge action={o.action} />
          )}
        </td>
        <td className="px-3 py-2 text-xs text-gray-400">{o.route_date || routeDate}</td>
        <td className="px-3 py-2">
          {isEditingTrans ? (
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={editTransDateValue}
                onChange={e => setEditTransDateValue(e.target.value)}
                className="px-1 py-0.5 bg-white border border-[#B08A3E] rounded text-xs focus:outline-none w-[120px]"
                data-testid={`trans-date-input-${o.order_id}`}
              />
              <button onClick={() => saveTransmissionDate(o, inspName)} className="p-0.5 rounded bg-[#6B8F62] text-white" data-testid={`trans-date-save-${o.order_id}`}>
                <Check className="h-2.5 w-2.5" />
              </button>
              <button onClick={() => setEditingTransDate(null)} className="p-0.5 rounded bg-gray-200 text-gray-500">
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ) : (
            <span
              className="text-xs text-gray-400 cursor-pointer hover:text-[#B08A3E] hover:underline"
              onClick={() => { setEditingTransDate(`${o.order_id}-${inspName}`); setEditTransDateValue(o.transmission_date || ""); }}
              title="Click to edit transmission date"
              data-testid={`trans-date-display-${o.order_id}`}
            >
              {o.transmission_date || "—"}
            </span>
          )}
        </td>
        {/* Follow-Up Flag */}
        <td className="px-3 py-2 text-center">
          {togglingFollowUp?.order_id === o.order_id && togglingFollowUp?.inspector_name === inspName ? (
            <div className="space-y-1.5" onClick={e => e.stopPropagation()}>
              <div className="flex gap-1">
                <button onClick={() => setFollowUpReason("Safeguard Follow-Up")} className={`px-1.5 py-1 rounded text-[9px] font-bold border ${followUpReason === "Safeguard Follow-Up" ? "bg-[#B08A3E] text-white border-[#B08A3E]" : "bg-white text-[#B08A3E] border-[#B08A3E]/40 hover:bg-[#B08A3E]/10"}`} data-testid={`followup-safeguard-${o.order_id}`}>Safeguard Follow-Up</button>
                <button onClick={() => setFollowUpReason("Inspector Follow-Up")} className={`px-1.5 py-1 rounded text-[9px] font-bold border ${followUpReason === "Inspector Follow-Up" ? "bg-[#C07070] text-white border-[#C07070]" : "bg-white text-[#C07070] border-[#C07070]/40 hover:bg-[#C07070]/10"}`} data-testid={`followup-inspector-${o.order_id}`}>Inspector Follow-Up</button>
              </div>
              {followUpReason && (
                <div className="flex items-center gap-1">
                  <input value={followUpComment} onChange={e => setFollowUpComment(e.target.value)} placeholder="Notes (optional)" className="px-1 py-0.5 bg-white border border-gray-300 rounded text-[10px] focus:outline-none w-[90px]" data-testid={`followup-comment-${o.order_id}`} />
                  <button onClick={confirmFollowUp} className="p-0.5 rounded bg-[#6B8F62] text-white" data-testid={`followup-confirm-${o.order_id}`}><Check className="h-2.5 w-2.5" /></button>
                  <button onClick={() => { setTogglingFollowUp(null); setFollowUpReason(""); }} className="p-0.5 rounded bg-gray-200 text-gray-500"><X className="h-2.5 w-2.5" /></button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1">
              <button
                onClick={() => hasFollowUp ? handleToggleFollowUp(o, inspName, false) : handleToggleFollowUp(o, inspName, true)}
                className={`p-1 rounded transition-colors ${hasFollowUp ? "bg-[#C07070]/15 text-[#C07070] hover:bg-[#C07070]/25" : "text-gray-300 hover:text-[#C07070] hover:bg-[#C07070]/10"}`}
                title={hasFollowUp ? `${o.follow_up_comment || "Follow-up"} (click to remove)` : "Flag for follow-up"}
                data-testid={`followup-toggle-${o.order_id}`}
              >
                <Flag className="h-3 w-3" />
              </button>
              {hasFollowUp && o.follow_up_comment && (
                <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${o.follow_up_comment?.includes("Inspector") ? "bg-[#C07070]/10 text-[#C07070]" : "bg-[#B08A3E]/10 text-[#B08A3E]"}`}>{o.follow_up_comment.includes("Inspector") ? "Insp F/U" : o.follow_up_comment.includes("Safeguard") ? "SFG F/U" : "F/U"}</span>
              )}
            </div>
          )}
        </td>
        <td className="px-3 py-2">
          {editing ? (
            <input value={editNotes} onChange={e => setEditNotes(e.target.value)}
              className="w-full px-1.5 py-0.5 bg-white border border-[#B08A3E] rounded text-xs focus:outline-none"
              placeholder="Notes..." data-testid="edit-notes-input"
            />
          ) : (
            <span className="text-xs text-gray-400 max-w-[120px] truncate block" title={o.notes}>{o.notes || "-"}</span>
          )}
        </td>
        <td className="px-3 py-1.5 text-right">
          {editing ? (
            <div className="flex items-center gap-1 justify-end">
              <button onClick={saveEdit} className="p-1 rounded bg-[#6B8F62] text-white hover:bg-[#5A7D53]" data-testid="save-edit-btn">
                <Check className="h-3 w-3" />
              </button>
              <button onClick={cancelEdit} className="p-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300" data-testid="cancel-edit-btn">
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-0.5 justify-end opacity-0 group-hover:opacity-100 transition-all">
              <button onClick={() => startEdit(o, inspName)}
                className="p-1 rounded text-gray-300 hover:text-[#B08A3E] hover:bg-[#B08A3E]/10"
                data-testid={`edit-btn-${o.order_id}`} title="Edit action & notes"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                onClick={() => { setReopenModal({ order_id: o.order_id, order_number: o.order_number, inspector_name: inspName }); setReopenDest("to_dispatch"); setReopenReason(""); }}
                className="p-1 rounded text-gray-300 hover:text-[#2F5D8A] hover:bg-[#2F5D8A]/10"
                data-testid={`reopen-closing-btn-${o.order_id}`} title="Reopen order"
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            </div>
          )}
        </td>
      </tr>
    );
  };

  const thCls = "text-left text-[9px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2";

  return (
    <Layout>
      <div className="space-y-4 max-w-[1400px] mx-auto" data-testid="daily-closing-page">
        {/* Header with Tabs */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1F2937] flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#B08A3E]" />
              Closing
            </h1>
            <p className="text-gray-500 text-sm">Manual archive — editable actions, filterable, aligned with QA</p>
          </div>
        </div>

        <PhaseCBanner department="Daily Close Review" testIdPrefix="daily-close" />

        {/* Tab Toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 w-fit" data-testid="closing-tabs">
          <button
            onClick={() => setActiveTab("daily")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
              activeTab === "daily"
                ? "bg-white text-[#1F2937] shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="tab-daily"
          >
            <FileText className="h-3.5 w-3.5" />
            Daily Archive
          </button>
          <button
            onClick={() => setActiveTab("weekly")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
              activeTab === "weekly"
                ? "bg-white text-[#1F2937] shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="tab-weekly"
          >
            <DollarSign className="h-3.5 w-3.5" />
            Weekly Closing
          </button>
        </div>

        {activeTab === "weekly" ? (
          <WeeklyClosingSection />
        ) : (
        <>
        {/* === DAILY ARCHIVE CONTENT === */}

        {message && (
          <div className={`rounded-lg border p-3 text-sm flex items-center justify-between ${message.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-[#A7C4A0]/15 border-[#A7C4A0]/40 text-[#4A7A56]'}`} data-testid="closing-message">
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Top Controls */}
        <div className="flex flex-wrap items-end gap-3 bg-white rounded-lg border border-gray-200 p-3" data-testid="daily-closing-filters">
          <div>
            <label className="block text-[9px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">Route Date</label>
            <input type="date" value={routeDate} onChange={e => setRouteDate(e.target.value)}
              className="px-2 py-1.5 bg-gray-50 border border-gray-300 rounded text-sm text-[#1F2937] focus:outline-none focus:border-[#D1A054]"
              data-testid="closing-route-date"
            />
          </div>
          <div>
            <label className="block text-[9px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">Inspector</label>
            <select value={selectedInspector}
              onChange={e => { setSelectedInspector(e.target.value); setShowAddPanel(false); setSelectedOrders(new Map()); }}
              className="px-2 py-1.5 bg-gray-50 border border-gray-300 rounded text-sm text-[#1F2937] focus:outline-none focus:border-[#D1A054] min-w-[200px]"
              data-testid="closing-inspector-select"
            >
              <option value="">Select Inspector...</option>
              {inspectors.map(i => <option key={i.id || i.name} value={i.name}>{i.name}</option>)}
            </select>
          </div>
          {selectedInspector && (
            <button onClick={() => setShowAddPanel(!showAddPanel)}
              className="px-3 py-1.5 bg-[#B08A3E] hover:bg-[#9A7735] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
              data-testid="add-orders-btn"
            >
              <Plus className="h-4 w-4" /> Add Orders
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => { setShowClearModal(true); setClearScope("all"); setClearReason(""); }}
              className="px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors border bg-white text-[#C07070] border-[#C07070]/25 hover:border-[#C07070]/50 hover:bg-[#C07070]/5"
              data-testid="clear-closing-btn"
            >
              <Eraser className="h-3.5 w-3.5" />
              Clear Closing
            </button>
            <button onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors border ${
                showFilters || hasActiveFilters ? 'bg-[#2F5D8A] text-white border-[#2F5D8A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#2F5D8A]/40'
              }`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="h-3.5 w-3.5" />
              Filters{hasActiveFilters ? " *" : ""}
            </button>
          </div>
        </div>

        {/* DAILY-CLOSE-RULE-001 — Special Buckets Warning (Angela 2026-05-18) */}
        {((specialBucketCounts._inactive_inspector || 0) + (specialBucketCounts._unregistered || 0) + (specialBucketCounts._empty_name || 0)) > 0 && (
          <div data-testid="special-buckets-warning" className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex items-start gap-3">
            <span className="text-amber-600 text-lg leading-none mt-0.5">⚠️</span>
            <div className="flex-1">
              <div className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-1">
                Closings em buckets especiais — não escondidos
              </div>
              <div className="text-xs text-amber-800 leading-relaxed">
                Esta data tem closings que <strong>antes ficavam invisíveis</strong> e agora aparecem por DAILY-CLOSE-RULE-001:
                {specialBucketCounts._unregistered > 0 && <span className="ml-2"><strong className="text-red-700">{specialBucketCounts._unregistered}</strong> não cadastrado</span>}
                {specialBucketCounts._inactive_inspector > 0 && <span className="ml-2"><strong className="text-orange-700">{specialBucketCounts._inactive_inspector}</strong> ex-inspetor</span>}
                {specialBucketCounts._empty_name > 0 && <span className="ml-2"><strong className="text-gray-700">{specialBucketCounts._empty_name}</strong> sem inspetor</span>}
                . Total visível: <strong>{totalClosingsVisible}</strong>.
              </div>
            </div>
          </div>
        )}

        {/* Summary Strip */}
        {summary.length > 0 && (
          <div className="flex items-center gap-4 bg-white rounded-lg border border-gray-200 px-4 py-2 flex-wrap" data-testid="payment-summary">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Summary:</span>
            <span className="text-xs text-[#1F2937] font-medium" data-testid="summary-total-orders">{totalOrders} total orders</span>
            <span className="text-xs text-[#6B8F62] font-medium" data-testid="summary-operational">{totalOrders - totalFollowUpOrders} operational</span>
            {totalFollowUpOrders > 0 && (
              <span className="flex items-center gap-1 text-xs text-[#C07070] font-semibold" data-testid="summary-followup">
                <Flag className="h-3 w-3" />
                {totalFollowUpOrders} follow-up
              </span>
            )}
            <span className="text-xs text-gray-500 font-medium" data-testid="summary-inspector-count">
              {regularInspectors.filter(g => g.orders.length > 0).length}/{regularInspectors.length} inspectors active
            </span>
            {totalSpecialBucketOrders > 0 && (
              <span className="text-xs text-amber-700 font-semibold" data-testid="summary-special-bucket-count">
                ⚠️ {totalSpecialBucketOrders} in special buckets
              </span>
            )}
          </div>
        )}

        {/* Table Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg border border-[#2F5D8A]/20 p-3 flex flex-wrap items-end gap-2" data-testid="table-filters">
            <div>
              <label className="block text-[9px] font-medium text-gray-400 uppercase mb-0.5">Search</label>
              <input value={tableFilters.search} onChange={e => setTableFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Order ID or Address..."
                className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs w-40 focus:outline-none focus:border-[#2F5D8A]"
              />
            </div>
            <div>
              <label className="block text-[9px] font-medium text-gray-400 uppercase mb-0.5">Company</label>
              <select value={tableFilters.company} onChange={e => setTableFilters(f => ({ ...f, company: e.target.value }))}
                className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#2F5D8A]"
              >
                <option value="">All</option>
                {uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-medium text-gray-400 uppercase mb-0.5">City</label>
              <select value={tableFilters.city} onChange={e => setTableFilters(f => ({ ...f, city: e.target.value }))}
                className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#2F5D8A]"
              >
                <option value="">All</option>
                {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-medium text-gray-400 uppercase mb-0.5">Inspector</label>
              <select value={tableFilters.inspector} onChange={e => setTableFilters(f => ({ ...f, inspector: e.target.value }))}
                className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#2F5D8A]"
              >
                <option value="">All</option>
                {uniqueInspectors.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-medium text-gray-400 uppercase mb-0.5">Action</label>
              <select value={tableFilters.action} onChange={e => setTableFilters(f => ({ ...f, action: e.target.value }))}
                className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#2F5D8A]"
              >
                <option value="">All</option>
                {uniqueActions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-medium text-gray-400 uppercase mb-0.5">Client</label>
              <input value={tableFilters.client} onChange={e => setTableFilters(f => ({ ...f, client: e.target.value }))}
                placeholder="Filter..." className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs w-28 focus:outline-none focus:border-[#2F5D8A]"
              />
            </div>
            <div>
              <label className="block text-[9px] font-medium text-gray-400 uppercase mb-0.5">Transmission Date</label>
              <input type="date" value={tableFilters.transmission_date}
                onChange={e => setTableFilters(f => ({ ...f, transmission_date: e.target.value }))}
                className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#2F5D8A]"
              />
            </div>
            {hasActiveFilters && (
              <button onClick={() => setTableFilters({ company: "", client: "", city: "", inspector: "", action: "", transmission_date: "", search: "" })}
                className="px-2 py-1 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        )}

        {/* Day Stats */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="text-gray-500 font-medium">{formatDate(routeDate)}</span>
          <span className="text-[#1F2937] font-bold">{totalOrders} orders archived</span>
          <span className="text-gray-400">{summary.length} inspectors</span>
          {hasActiveFilters && <span className="text-[#2F5D8A] font-medium">Showing {filteredOrders.length} of {totalOrders}</span>}
        </div>

        {/* ADD ORDERS PANEL */}
        {showAddPanel && selectedInspector && (
          <div className="bg-white rounded-lg border-2 border-[#B08A3E]/40 overflow-hidden" data-testid="add-orders-panel">
            <div className="bg-[#B08A3E]/5 px-4 py-3 border-b border-[#B08A3E]/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-[#B08A3E]" />
                <span className="font-semibold text-[#1F2937] text-sm">Add Orders for {selectedInspector}</span>
                <span className="text-xs text-gray-400">{selectedOrders.size} selected</span>
              </div>
              <button onClick={() => setShowAddPanel(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by Order ID, address, or client..."
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#B08A3E]"
                  data-testid="order-search-input"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {searchResults.map(o => (
                    <div key={o.id} onClick={() => !existingIds.has(o.id) && toggleOrder(o)}
                      className={`flex items-center gap-3 px-3 py-2 text-xs border-b border-gray-50 cursor-pointer transition-colors ${
                        selectedOrders.has(o.id) ? 'bg-[#B08A3E]/10' : existingIds.has(o.id) ? 'bg-gray-50 opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                      }`}
                    >
                      {selectedOrders.has(o.id) ? <Check className="h-3.5 w-3.5 text-[#B08A3E] flex-shrink-0" /> : <Plus className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />}
                      <span className="font-medium text-[#1F2937] w-20">{o.order_number || o.id.slice(-6)}</span>
                      <span className="text-gray-500 w-24 truncate">{o.company}</span>
                      <span className="text-gray-500 flex-1 truncate">{o.address}</span>
                      <span className="text-gray-400">{o.city}</span>
                    </div>
                  ))}
                </div>
              )}
              {inspectorOrders.length > 0 && !searchQuery && (
                <div>
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
                    {selectedInspector}'s Orders ({inspectorOrders.length})
                    <button onClick={() => {
                      const next = new Map(selectedOrders);
                      inspectorOrders.forEach(o => { if (!existingIds.has(o.id) && !next.has(o.id)) next.set(o.id, { order: o, action: "Completed", notes: "" }); });
                      setSelectedOrders(next);
                    }} className="ml-2 text-[#B08A3E] hover:underline normal-case" data-testid="select-all-inspector-orders">Select All</button>
                  </p>
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {inspectorOrders.map(o => (
                      <div key={o.id} onClick={() => !existingIds.has(o.id) && toggleOrder(o)}
                        className={`flex items-center gap-3 px-3 py-1.5 text-xs border-b border-gray-50 cursor-pointer transition-colors ${
                          selectedOrders.has(o.id) ? 'bg-[#B08A3E]/10' : existingIds.has(o.id) ? 'bg-gray-50 opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                        }`}
                      >
                        {selectedOrders.has(o.id) ? <Check className="h-3 w-3 text-[#B08A3E]" /> : <div className="h-3 w-3 border border-gray-300 rounded" />}
                        <span className="font-medium text-[#1F2937] w-20">{o.order_number || o.id.slice(-6)}</span>
                        <span className="text-gray-500 w-20 truncate">{o.company}</span>
                        <span className="text-gray-500 flex-1 truncate">{o.address}, {o.city}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedOrders.size > 0 && (
                <div>
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Define Action for Each Order ({selectedOrders.size})</p>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-50 max-h-64 overflow-y-auto">
                    {Array.from(selectedOrders.entries()).map(([id, entry]) => (
                      <div key={id} className="flex items-center gap-2 px-3 py-2" data-testid={`selected-order-${id}`}>
                        <button onClick={() => toggleOrder(entry.order)} className="text-gray-300 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                        <span className="text-xs font-medium text-[#1F2937] w-20">{entry.order.order_number || id.slice(-6)}</span>
                        <span className="text-xs text-gray-400 w-32 truncate">{entry.order.address}</span>
                        <select value={entry.action} onChange={e => updateOrderAction(id, e.target.value)}
                          className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-xs text-[#1F2937] focus:outline-none"
                          data-testid={`action-select-${id}`}
                        >
                          {ACTION_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                        <input value={entry.notes} onChange={e => updateOrderNotes(id, e.target.value)}
                          placeholder="Notes..." className="flex-1 px-2 py-1 bg-gray-50 border border-gray-300 rounded text-xs focus:outline-none"
                          data-testid={`notes-input-${id}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end mt-3">
                    <button onClick={handleSave} disabled={saving || selectedOrders.size === 0}
                      className="px-4 py-2 bg-[#6B8F62] hover:bg-[#5A7D53] text-white text-sm font-semibold rounded-lg flex items-center gap-2 disabled:opacity-40 transition-colors"
                      data-testid="save-closing-btn"
                    >
                      <Save className="h-4 w-4" /> {saving ? "Saving..." : `Save ${selectedOrders.size} Orders`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* BATCH ACTIONS TOOLBAR */}
        {selectedClosingIds.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap px-4 py-2.5 bg-[#2F5D8A]/10 border border-[#2F5D8A]/30 rounded-lg mb-3" data-testid="route-date-edit-toolbar">
            <span className="text-[#2F5D8A] text-sm font-medium">{selectedClosingIds.size} selected</span>
            <button onClick={() => { setShowRouteDateEdit(true); setNewRouteDate(routeDate); }}
              className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
              data-testid="edit-route-date-btn"
            >
              <Calendar className="h-3.5 w-3.5" /> Edit Route Date
            </button>
            <button
              onClick={() => {
                // Open the Update Transmission flow for the first selected order.
                // If multiple are selected, the modal will apply the chosen
                // transmission (Vitória / Aline / Aly + Paid By) to all of them.
                if (selectedClosingIds.size === 0) return;
                const ids = Array.from(selectedClosingIds);
                const first = allOrders.find(o => o.order_id === ids[0]);
                if (!first) return;
                openTransmitModal({ ...first, _bulk_order_ids: ids, _inspector_name: first.inspector_display });
              }}
              className="px-3 py-1.5 bg-[#6B5B8D] hover:bg-[#5a4c7a] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
              data-testid="bulk-update-transmission-btn"
            >
              <Radio className="h-3.5 w-3.5" /> Update Transmission
            </button>
            <button onClick={() => setSelectedClosingIds(new Set())}
              className="px-3 py-1.5 text-gray-500 hover:text-[#1F2937] text-xs ml-auto"
              data-testid="clear-closing-selection"
            >
              Clear
            </button>
          </div>
        )}

        {/* EXISTING CLOSINGS */}
        {loading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
        ) : summary.length === 0 ? (
          <div className="text-center py-16 text-gray-400" data-testid="closing-empty">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No closing records for this date</p>
            <p className="text-xs mt-1">Select an inspector and add orders to create a closing</p>
          </div>
        ) : hasActiveFilters ? (
          /* Filtered flat table view */
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="filtered-closing-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80">
                    <th className={`${thCls} w-8`}>
                      <input type="checkbox"
                        checked={filteredOrders.length > 0 && filteredOrders.every(o => selectedClosingIds.has(o.order_id))}
                        onChange={(e) => {
                          const ids = filteredOrders.map(o => o.order_id);
                          setSelectedClosingIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) { ids.forEach(id => next.add(id)); }
                            else { ids.forEach(id => next.delete(id)); }
                            return next;
                          });
                        }}
                        className="rounded border-gray-300 text-[#2F5D8A] focus:ring-[#2F5D8A] h-3.5 w-3.5"
                        data-testid="select-all-filtered"
                      />
                    </th>
                    <th className={thCls}>Order</th>
                    <th className={thCls}>Company</th>
                    <th className={thCls}>Client</th>
                    <th className={thCls}>Address</th>
                    <th className={thCls}>Action</th>
                    <th className={thCls}>Route Date</th>
                    <th className={thCls}>Transmission</th>
                    <th className={`${thCls} text-center`}>Follow-Up</th>
                    <th className={thCls}>Notes</th>
                    <th className={`${thCls} w-16`}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o, idx) => renderOrderRow(o, o.inspector_display, idx))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grouped by inspector (default view) */
          <div className="space-y-3" data-testid="closing-summary-list">
            {/* Sprint B-2 (Angela 2026-05-18): Batch action bar + select all */}
            {summary.some(g => g.orders.length > 0) && (
              <div className="bg-[#F5F2EC] border border-[#E5DFD3] rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-3" data-testid="batch-action-bar">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={batchSelected.size > 0 && summary.flatMap(g => g.orders).filter(o => o.closing_id).every(o => batchSelected.has(o.closing_id))}
                    onChange={e => {
                      if (e.target.checked) {
                        const all = new Set(summary.flatMap(g => g.orders).map(o => o.closing_id).filter(Boolean));
                        setBatchSelected(all);
                      } else { setBatchSelected(new Set()); }
                    }}
                    className="rounded border-gray-400 h-4 w-4"
                    data-testid="batch-select-all"
                  />
                  Selecionar todas
                </label>
                <span className="text-xs text-gray-600" data-testid="batch-selected-count">{batchSelected.size} selected</span>
                <select
                  value={batchAction}
                  onChange={e => setBatchAction(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded"
                  data-testid="batch-action-select"
                >
                  <option value="">— Ação em lote —</option>
                  <option value="Completed">Marcar Completed</option>
                  <option value="No Show">Marcar No Show</option>
                  <option value="Returned">Devolver (Returned)</option>
                  <option value="Hold">Mover para Hold</option>
                  <option value="Follow Up">Marcar Follow Up</option>
                  <option value="Rescheduled">Reschedule</option>
                </select>
                <button
                  disabled={batchSelected.size === 0 || !batchAction}
                  onClick={() => setBatchConfirmOpen(true)}
                  className="px-3 py-1 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-bold rounded disabled:bg-gray-300"
                  data-testid="batch-action-apply"
                >
                  Aplicar a {batchSelected.size} ordem(ns)
                </button>
              </div>
            )}

            {summary.map(group => {
              const isExp = expandedInsp === group.inspector;
              return (
                <div key={group.inspector} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`closing-group-${group.inspector}`}>
                  <div onClick={() => setExpandedInsp(isExp ? null : group.inspector)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50/50 transition-colors cursor-pointer"
                    role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setExpandedInsp(isExp ? null : group.inspector); }}
                  >
                    <div className="flex items-center gap-3">
                      <User className={`h-4 w-4 ${group.is_special_bucket ? (group.badge_color === 'red' ? 'text-red-500' : group.badge_color === 'orange' ? 'text-orange-500' : 'text-gray-500') : 'text-[#B08A3E]'}`} />
                      <span className="font-semibold text-[#1F2937] text-sm">{group.label || group.inspector}</span>
                      {group.is_special_bucket && (
                        <span data-testid={`bucket-badge-${group.inspector}`} className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${
                          group.badge_color === 'red' ? 'bg-red-100 text-red-700' :
                          group.badge_color === 'orange' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-200 text-gray-700'
                        }`} title={group.description}>
                          {group.badge_color === 'red' ? '⚠️ Não cadastrado' :
                           group.badge_color === 'orange' ? '🟠 Ex-inspetor' :
                           '⬜ Sem inspetor'}
                        </span>
                      )}
                      <span className="text-gray-400 text-xs">{group.orders.length} orders</span>
                      {group.orders.filter(o => o.follow_up).length > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] font-bold text-[#C07070]">
                          <Flag className="h-2.5 w-2.5" />
                          {group.orders.filter(o => o.follow_up).length} flagged
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        {Object.entries(group.counts).map(([action, count]) => (
                          <span key={action} className="flex items-center gap-0.5">
                            <ActionBadge action={action} />
                            <span className="text-[10px] text-gray-500 font-medium">{count}</span>
                          </span>
                        ))}
                      </div>
                      {isExp ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                    </div>
                  </div>
                  {isExp && (
                    <div className="border-t border-gray-100">
                      <RouteArchivePanel inspector={group.inspector} routeDate={routeDate} />
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50/80">
                              <th className={`${thCls} w-8`}>
                                <input type="checkbox"
                                  checked={group.orders.length > 0 && group.orders.every(o => selectedClosingIds.has(o.order_id))}
                                  onChange={(e) => {
                                    const ids = group.orders.map(o => o.order_id);
                                    setSelectedClosingIds(prev => {
                                      const next = new Set(prev);
                                      if (e.target.checked) { ids.forEach(id => next.add(id)); }
                                      else { ids.forEach(id => next.delete(id)); }
                                      return next;
                                    });
                                  }}
                                  className="rounded border-gray-300 text-[#2F5D8A] focus:ring-[#2F5D8A] h-3.5 w-3.5"
                                  data-testid={`select-all-${group.inspector}`}
                                />
                              </th>
                              <th className={thCls}>Order</th>
                              <th className={thCls}>Company</th>
                              <th className={thCls}>Client</th>
                              <th className={thCls}>Address</th>
                              <th className={thCls}>Action</th>
                              <th className={thCls}>Route Date</th>
                              <th className={thCls}>Transmission</th>
                              <th className={`${thCls} text-center`}>Follow-Up</th>
                              <th className={thCls}>Notes</th>
                              <th className={`${thCls} w-16`}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.orders.map((o, idx) => renderOrderRow({ ...o, inspector_display: group.inspector }, group.inspector, idx))}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-4 py-2 bg-gray-50/50 border-t border-gray-100 flex justify-end">
                        <button onClick={() => handleDeleteClosing(group.inspector)}
                          className="px-2 py-1 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                          data-testid={`delete-closing-${group.inspector}`}
                        >
                          <Trash2 className="h-3 w-3" /> Delete Closing
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        )}

        {/* Clear Daily Closing Modal */}
        {showClearModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="clear-closing-modal">
            <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-[#C07070]/10 rounded-lg">
                  <Eraser className="h-5 w-5 text-[#C07070]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1F2937]">Clear Daily Closing</h3>
                  <p className="text-xs text-gray-500">Records will be archived safely, not permanently deleted</p>
                </div>
                <button onClick={() => setShowClearModal(false)} className="ml-auto p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="bg-[#FDF8F0] border border-[#B08A3E]/20 rounded-lg p-3 mb-4">
                <p className="text-xs text-[#7A6535]">
                  <span className="font-semibold">Date:</span> {routeDate} &nbsp;|&nbsp;
                  <span className="font-semibold">Orders:</span> {totalOrders}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Scope</label>
                <div className="space-y-1.5">
                  <button
                    onClick={() => setClearScope("all")}
                    className={`w-full px-3 py-2 rounded-lg border text-left text-xs transition-all ${
                      clearScope === "all" ? "bg-[#C07070]/8 border-[#C07070]/40 ring-1 ring-[#C07070]" : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid="clear-scope-all"
                  >
                    <span className="font-semibold text-[#1F2937]">Clear All Inspectors</span>
                    <span className="text-gray-400 ml-1">({totalOrders} orders on {routeDate})</span>
                  </button>
                  {summary.map(g => (
                    <button
                      key={g.inspector}
                      onClick={() => setClearScope(g.inspector)}
                      className={`w-full px-3 py-2 rounded-lg border text-left text-xs transition-all ${
                        clearScope === g.inspector ? "bg-[#C07070]/8 border-[#C07070]/40 ring-1 ring-[#C07070]" : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`clear-scope-${g.inspector}`}
                    >
                      <span className="font-semibold text-[#1F2937]">{g.label || g.inspector}</span>
                      {g.is_special_bucket && (
                        <span className={`ml-1 text-[9px] font-bold uppercase ${
                          g.badge_color === 'red' ? 'text-red-600' :
                          g.badge_color === 'orange' ? 'text-orange-600' : 'text-gray-500'
                        }`}>
                          [special]
                        </span>
                      )}
                      <span className="text-gray-400 ml-1">({g.orders.length} orders)</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Reason <span className="text-red-600">(required, min 20 chars)</span></label>
                <input
                  value={clearReason}
                  onChange={e => setClearReason(e.target.value)}
                  placeholder="Why is this closing being cleared (DAILY-CLOSE-RULE-001)..."
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#C07070] focus:outline-none"
                  data-testid="clear-reason-input"
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-[10px] text-gray-400 space-y-0.5">
                <p>Historical data will be preserved in archive</p>
                <p>Order history, logs, and financial records remain intact</p>
                <p>You can redo closing from Dispatch after clearing</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearModal(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg transition-colors"
                  data-testid="clear-closing-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearClosing}
                  disabled={clearProcessing || totalOrders === 0}
                  className="flex-1 px-4 py-2.5 bg-[#C07070] hover:bg-[#A85858] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
                  data-testid="clear-closing-confirm"
                >
                  <Eraser className="h-4 w-4" />
                  {clearProcessing ? "Clearing..." : clearScope === "all" ? `Clear All (${totalOrders})` : `Clear ${clearScope}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reopen from Daily Closing Modal */}
        {reopenModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="reopen-closing-modal">
            <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-[#2F5D8A]/10 rounded-lg">
                  <RotateCcw className="h-5 w-5 text-[#2F5D8A]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1F2937]">Reopen Order</h3>
                  <p className="text-xs text-gray-500">Order {reopenModal.order_number || reopenModal.order_id.slice(-6)} — {reopenModal.inspector_name}</p>
                </div>
                <button onClick={() => setReopenModal(null)} className="ml-auto p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Destination *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setReopenDest("to_inspector")}
                    className={`px-3 py-3 rounded-lg border text-left transition-all ${
                      reopenDest === "to_inspector"
                        ? "bg-[#B08A3E]/10 border-[#B08A3E] ring-1 ring-[#B08A3E]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid="reopen-dest-inspector"
                  >
                    <p className="text-xs font-semibold text-[#1F2937]">Return to Inspector</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Back to {reopenModal.inspector_name}</p>
                  </button>
                  <button
                    onClick={() => setReopenDest("to_dispatch")}
                    className={`px-3 py-3 rounded-lg border text-left transition-all ${
                      reopenDest === "to_dispatch"
                        ? "bg-[#2F5D8A]/10 border-[#2F5D8A] ring-1 ring-[#2F5D8A]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid="reopen-dest-dispatch"
                  >
                    <p className="text-xs font-semibold text-[#1F2937]">Return to Dispatch</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Unassigned, ready to route</p>
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Reason for Reopening</label>
                <textarea
                  value={reopenReason}
                  onChange={e => setReopenReason(e.target.value)}
                  placeholder="Optional reason..."
                  rows={2}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#2F5D8A] focus:outline-none resize-none"
                  data-testid="reopen-closing-reason"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setReopenModal(null)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg transition-colors"
                  data-testid="reopen-closing-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReopen}
                  disabled={reopenProcessing}
                  className="flex-1 px-4 py-2.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
                  data-testid="reopen-closing-confirm"
                >
                  <Send className="h-4 w-4" />
                  {reopenProcessing ? "Reopening..." : reopenDest === "to_inspector" ? "Reopen to Inspector" : "Reopen to Dispatch"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Route Date Edit Modal */}
        {showRouteDateEdit && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="route-date-edit-modal">
            <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-[#2F5D8A]/10 rounded-lg">
                  <Calendar className="h-5 w-5 text-[#2F5D8A]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1F2937]">Edit Route Date</h3>
                  <p className="text-xs text-gray-500">{selectedClosingIds.size} order{selectedClosingIds.size !== 1 ? "s" : ""} selected</p>
                </div>
                <button onClick={() => setShowRouteDateEdit(false)} className="ml-auto p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-[#2F5D8A] uppercase tracking-wider mb-1">New Route Date</label>
                <input
                  type="date"
                  value={newRouteDate}
                  onChange={e => setNewRouteDate(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-[#2F5D8A]/30 rounded-lg text-sm font-medium text-[#1F2937] focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                  data-testid="new-route-date-input"
                />
                <p className="text-[10px] text-gray-400 mt-1">Current: {routeDate} | New: {newRouteDate}</p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowRouteDateEdit(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg transition-colors"
                  data-testid="route-date-edit-cancel"
                >Cancel</button>
                <button onClick={handleBatchRouteDateUpdate}
                  disabled={routeDateEditProcessing || !newRouteDate || newRouteDate === routeDate}
                  className="flex-1 px-4 py-2.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
                  data-testid="route-date-edit-confirm"
                >
                  <Calendar className="h-4 w-4" />
                  {routeDateEditProcessing ? "Updating..." : "Update Date"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Update Transmission modal — reuses the existing /orders/{id}/transmit flow */}
        {transmitModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => !transmitSubmitting && setTransmitModal(null)}>
            <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-md shadow-2xl"
              onClick={e => e.stopPropagation()} data-testid="daily-closing-transmit-modal">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Radio className="h-4 w-4 text-[#6B5B8D]" /> Update Transmission
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {transmitModal._bulk_order_ids && transmitModal._bulk_order_ids.length > 1
                      ? <>{transmitModal._bulk_order_ids.length} orders selected</>
                      : <>Order #{transmitModal.order_number}</>}
                  </p>
                </div>
                <button onClick={() => setTransmitModal(null)} className="p-1 rounded hover:bg-gray-100" data-testid="transmit-close">
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>
              {transmitModal.transmitted && !(transmitModal._bulk_order_ids && transmitModal._bulk_order_ids.length > 1) && (
                <div className="mb-3 text-[11px] text-[#6B5B8D] bg-[#F0EDF5] border border-[#6B5B8D]/20 rounded-md px-2.5 py-1.5">
                  Already marked as transmitted. Changing the values below will overwrite the previous transmission record.
                </div>
              )}
              {transmitModal._bulk_order_ids && transmitModal._bulk_order_ids.length > 1 && (
                <div className="mb-3 text-[11px] text-[#6B5B8D] bg-[#F0EDF5] border border-[#6B5B8D]/20 rounded-md px-2.5 py-1.5">
                  The choice below will be applied to all {transmitModal._bulk_order_ids.length} selected orders.
                </div>
              )}

              <div className="mb-4">
                <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-2 font-semibold">Transmitted By *</label>
                <div className="grid grid-cols-3 gap-2">
                  {["Vitória", "Aline", "Aly"].map(op => (
                    <button
                      key={op}
                      onClick={() => setTransmitOperator(op)}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all border ${
                        transmitOperator === op
                          ? "bg-[#D1A054] text-[#1F2937] border-[#D1A054] ring-2 ring-[#D1A054]/30"
                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                      }`}
                      data-testid={`dc-transmit-op-${op.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'')}`}
                    >
                      {op}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-2 font-semibold">Paid By *</label>
                <div className="grid grid-cols-2 gap-2">
                  {["Inspector", "Office"].map(p => (
                    <button
                      key={p}
                      onClick={() => { setTransmitPaidBy(p); if (p === "Inspector") { setTransmitOfficeReason(""); setTransmitOfficeReasonDetail(""); } }}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all border ${
                        transmitPaidBy === p
                          ? p === "Inspector"
                            ? "bg-[#2F5D8A] text-white border-[#2F5D8A] ring-2 ring-[#2F5D8A]/20"
                            : "bg-[#5A4F7A] text-white border-[#5A4F7A] ring-2 ring-[#5A4F7A]/20"
                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                      }`}
                      data-testid={`dc-transmit-paid-${p.toLowerCase()}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {transmitPaidBy === "Office" && (
                <div className="mb-4 p-3 bg-[#F0EDF5] border border-[#6B5B8D]/25 rounded-lg">
                  <label className="block text-[10px] text-[#6B5B8D] uppercase tracking-wide mb-2 font-semibold">Office Payment Reason *</label>
                  <select
                    value={transmitOfficeReason}
                    onChange={e => setTransmitOfficeReason(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                    data-testid="dc-transmit-office-reason"
                  >
                    <option value="">Select a reason...</option>
                    {["Order already closed by office","Office requested support","Inspector not responsible","Internal office decision","Billing adjustment","Other"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {transmitOfficeReason === "Other" && (
                    <textarea
                      value={transmitOfficeReasonDetail}
                      onChange={e => setTransmitOfficeReasonDetail(e.target.value)}
                      placeholder="Describe the reason..."
                      className="w-full mt-2 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] min-h-[60px]"
                      data-testid="dc-transmit-office-reason-detail"
                    />
                  )}
                </div>
              )}

              <div className="flex gap-2 mt-5">
                <button
                  onClick={confirmTransmit}
                  disabled={transmitSubmitting || !transmitOperator || !transmitPaidBy || (transmitPaidBy === "Office" && !transmitOfficeReason) || (transmitPaidBy === "Office" && transmitOfficeReason === "Other" && !transmitOfficeReasonDetail.trim())}
                  className="flex-1 px-4 py-2.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="dc-transmit-confirm"
                >
                  <Radio className="h-4 w-4" />
                  {transmitSubmitting ? "Saving..." : (transmitModal.transmitted ? "Update Transmission" : "Confirm Transmission")}
                </button>
                {transmitModal.transmitted && !(transmitModal._bulk_order_ids && transmitModal._bulk_order_ids.length > 1) && (
                  <button
                    onClick={undoTransmit}
                    disabled={transmitSubmitting}
                    className="px-3 py-2.5 bg-[#C07070]/10 hover:bg-[#C07070]/20 text-[#C07070] text-xs font-semibold rounded-lg flex items-center gap-1"
                    data-testid="dc-transmit-undo"
                    title="Undo transmission"
                  >
                    <Undo2 className="h-3.5 w-3.5" /> Undo
                  </button>
                )}
                <button
                  onClick={() => setTransmitModal(null)}
                  disabled={transmitSubmitting}
                  className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm rounded-lg"
                  data-testid="dc-transmit-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sprint B-2 — Batch action confirmation modal */}
        {batchConfirmOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1200] p-4" data-testid="batch-confirm-modal">
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl p-5 space-y-4">
              <div>
                <h2 className="text-base font-bold text-[#1F2937]">Confirmar Ação em Lote</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Aplicar <strong>{batchAction}</strong> em <strong>{batchSelected.size}</strong> ordem(ns)?
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Motivo <span className="text-red-600">(obrigatório, min 20 chars)</span></label>
                <textarea
                  value={batchReason}
                  onChange={e => setBatchReason(e.target.value)}
                  rows={3}
                  placeholder="DAILY-CLOSE-RULE-001: descreva o motivo desta ação em lote..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#2F5D8A] focus:outline-none resize-none"
                  data-testid="batch-confirm-reason"
                />
                <p className="text-[10px] text-gray-400 mt-1">{batchReason.length}/20+ chars required</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setBatchConfirmOpen(false); setBatchReason(""); }}
                  disabled={batchSubmitting}
                  className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg"
                  data-testid="batch-confirm-cancel"
                >
                  Cancelar
                </button>
                <button
                  disabled={batchSubmitting || batchReason.trim().length < 20}
                  onClick={async () => {
                    setBatchSubmitting(true);
                    try {
                      const res = await axios.post(`${API}/daily-close/batch-action`, {
                        closing_ids: Array.from(batchSelected),
                        action: batchAction,
                        reason: batchReason.trim(),
                      });
                      setMessage({ type: "success", text: `Updated ${res.data.updated} closings.` });
                      setBatchSelected(new Set());
                      setBatchAction("");
                      setBatchReason("");
                      setBatchConfirmOpen(false);
                      fetchSummary();
                    } catch (e) {
                      setMessage({ type: "error", text: e.response?.data?.detail || "Failed" });
                    } finally {
                      setBatchSubmitting(false);
                    }
                  }}
                  className="flex-1 py-2 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-sm font-bold rounded-lg disabled:bg-gray-300"
                  data-testid="batch-confirm-apply"
                >
                  {batchSubmitting ? "Aplicando..." : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DailyClosingPage;
